// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/HomeMasthead.tsx
// 🔧 PURPOSE: L'intestazione della Home, come quella delle email del
//             channel manager: fascia espresso (notte #1a1a1a in scuro, lo
//             stesso colore della barra di stato), lo stemma nel cerchio
//             avorio, "Nonna Vittoria" in Fraunces e "APARTMENTS" in oro.
//             Sotto: il filo d'oro, l'occhiello ("BUONGIORNO", "TI
//             ASPETTIAMO"), il saluto grande e una riga ("Notte 3 di 7 ·
//             Appartamento 101").
//
//             A destra la pillola della lingua ("IT", apre il foglio delle
//             lingue) e, con l'ospite dentro, l'avatar con l'iniziale: il
//             menu con La mia prenotazione, Lingua ed Esci (l'uscita deve
//             restare a portata di mano).
//
//             È nel flusso della pagina, non fissa: il margine per la barra
//             di stato è var(--safe-area-inset-top), che la riga della
//             consegna azzera quando c'è (lo paga lei), come nelle altre
//             pagine rifatte.
//
//             LA FOTO DELL'APPARTAMENTO (photoUrl, da booking/info: la foto
//             principale dell'appartamento prenotato; viste B, S e A). Scelta
//             del proprietario: la versione "B" del mockup, cioè una fascia
//             in cui la foto si vede davvero, fra la riga del marchio e il
//             saluto. Dal basso in alto:
//               1. il fondo espresso (notte in scuro) di sempre;
//               2. la foto, virata al marrone (sepia/saturate/brightness,
//                  un po' più scura in scuro), che compare in dissolvenza
//                  quando è caricata e sparisce se non si carica;
//               3. il velo espresso: .88 in alto (si fonde con la barra di
//                  stato), .80 dietro al marchio, .44 nella fascia della foto,
//                  .82-.86 dietro al testo e sotto la card (in scuro, notte
//                  più fitta).
//             Con una foto l'intestazione è più alta di 80px (la fascia),
//             già mentre la foto si carica: niente salto quando arriva. Se
//             non si carica, o non c'è (channel manager vecchio, nessuna
//             foto https), l'intestazione è esattamente quella di prima.
//             Solo con la foto visibile: la riga del marchio nella variante
//             "overlay", la riga sotto il saluto in pergamena invece che in
//             beige e un'ombra leggera sotto il testo. Contrasti AA misurati
//             anche con una foto tutta bianca (resoconto hero-impl).
//
//             BrandBar (solo la riga con stemma, lingua e avatar) serve anche
//             sopra le foto del carosello della vista ospite (variante
//             "overlay", trasparente).
// ============================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Briefcase, Globe, LogOut, User } from 'lucide-react';
import { cn } from '../../lib/utils';
import { appLanguageOf, languageLabel } from '../../lib/locale';
import { openLanguageSheet } from '../LanguageSheet';
import { useGuestSession } from '../../hooks/useGuestSession';
import { apartmentPhotoProps, type ApartmentPhotoProps } from '../../lib/apartmentPhoto';
import { Eyebrow, FOCUS_RING_ON_DARK, GoldRule } from './ui';

/** Lo stemma ritagliato dal logo (precache del service worker). */
export const EMBLEM_SRC = '/icons/logo-emblem.png';

/** Lo stemma nel cerchio avorio (44px nell'intestazione, 56 nella card della reception). */
export const Emblem: React.FC<{ size?: number; className?: string }> = ({ size = 44, className }) => (
  <span
    aria-hidden="true"
    style={{ width: size, height: size }}
    className={cn('flex flex-shrink-0 items-center justify-center rounded-full bg-nva-bone', className)}
  >
    <img
      src={EMBLEM_SRC}
      alt=""
      width={Math.round(size * 0.86)}
      height={Math.round(size * 0.86)}
      className="block"
      decoding="async"
    />
  </span>
);

// ---- La pillola della lingua ---------------------------------------------------

const LanguagePill: React.FC<{ overlay?: boolean }> = ({ overlay = false }) => {
  const { t, i18n } = useTranslation();
  const code = appLanguageOf(i18n.language);
  return (
    <button
      type="button"
      onClick={openLanguageSheet}
      aria-label={t('home.masthead.language', { lang: languageLabel(code) })}
      className={cn('flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full', FOCUS_RING_ON_DARK)}
    >
      <span
        className={cn(
          'flex h-9 min-w-[44px] items-center justify-center rounded-full border px-2.5 text-[12px] font-bold tracking-[0.08em] text-nva-bone',
          overlay ? 'border-nva-bone/40 bg-nva-espresso/35' : 'border-nva-bone/30',
        )}
      >
        {code.toUpperCase()}
      </span>
    </button>
  );
};

// ---- L'avatar e il suo menu ------------------------------------------------------

const MENU_ITEM =
  'flex min-h-[48px] w-full items-center gap-3 px-4 text-left text-[15px] font-semibold text-nva-espresso transition-colors ' +
  'hover:bg-nva-cream/70 focus:outline-none focus-visible:bg-nva-cream dark:text-stone-100 dark:hover:bg-stone-800 dark:focus-visible:bg-stone-800';

const AccountMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { guestName, logout } = useGuestSession();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const initial = (guestName || '').trim().charAt(0).toUpperCase();

  // Fuori dal menu o Esc: si chiude (Esc riporta il focus sull'avatar).
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e: PointerEvent | MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer as unknown as EventListener, { passive: true });
    document.addEventListener('keydown', onKey);
    // Il primo elemento prende il focus: da tastiera si scorre con Tab.
    const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
    first?.focus();
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer as unknown as EventListener);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (action: () => void) => () => {
    setOpen(false);
    action();
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('home.masthead.menuLabel')}
        className={cn('flex h-11 w-11 items-center justify-center rounded-full', FOCUS_RING_ON_DARK)}
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-nva-gold text-[14px] font-extrabold text-nva-espresso">
          {initial || <User className="h-4 w-4" aria-hidden="true" />}
        </span>
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label={t('home.masthead.menuLabel')}
          className="absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-2xl border border-nva-edge bg-nva-card py-1.5 shadow-nva-lift dark:border-stone-700 dark:bg-stone-900 dark:shadow-none"
        >
          <button type="button" role="menuitem" onClick={choose(() => navigate('/my-stay'))} className={MENU_ITEM}>
            <Briefcase className="h-[18px] w-[18px] text-nva-gold-text dark:text-nva-gold-dark" aria-hidden="true" />
            {t('home.masthead.myBooking')}
          </button>
          <button type="button" role="menuitem" onClick={choose(openLanguageSheet)} className={MENU_ITEM}>
            <Globe className="h-[18px] w-[18px] text-nva-gold-text dark:text-nva-gold-dark" aria-hidden="true" />
            {t('language.title')}
          </button>
          <span aria-hidden="true" className="mx-4 my-1 block h-px bg-nva-hairline dark:bg-stone-800" />
          <button type="button" role="menuitem" onClick={choose(logout)} className={MENU_ITEM}>
            <LogOut className="h-[18px] w-[18px] text-nva-gold-text dark:text-nva-gold-dark" aria-hidden="true" />
            {t('hero.logout')}
          </button>
        </div>
      )}
    </div>
  );
};

// ---- La riga del marchio -----------------------------------------------------------

interface BrandBarProps {
  /** "overlay": sopra una foto (pillola con il fondo velato, "APARTMENTS" più chiaro). */
  variant?: 'solid' | 'overlay';
  /** L'avatar con il menu dell'account (solo con l'ospite dentro). */
  account?: boolean;
  className?: string;
}

/** Stemma, "Nonna Vittoria / APARTMENTS", lingua e avatar. */
export const BrandBar: React.FC<BrandBarProps> = ({ variant = 'solid', account = false, className }) => {
  const overlay = variant === 'overlay';
  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <Emblem size={44} />
      <div className="flex min-w-0 flex-grow flex-col gap-0.5">
        {/* Il marchio resta scritto così in tutte le lingue. */}
        <span className="font-display text-[17px] font-bold leading-none text-nva-bone">Nonna Vittoria</span>
        <span
          className={cn(
            'text-[9.5px] font-bold uppercase tracking-brand',
            overlay ? 'text-nva-gold-light' : 'text-nva-gold',
          )}
        >
          Apartments
        </span>
      </div>
      <LanguagePill overlay={overlay} />
      {account ? <AccountMenu /> : null}
    </div>
  );
};

// ---- La foto dell'appartamento ------------------------------------------------------

/**
 * La fascia della foto (versione B): 58px più lo spazio fra le righe (22px)
 * = l'intestazione cresce di 80px, e la card con le tessere scende di tanto.
 */
const PHOTO_WINDOW_PX = 58;

/** Da dove parte la pagina sotto la barra di stato. */
const SAFE_TOP = 'var(--safe-area-inset-top, 0px)';

/**
 * Il velo sopra la foto, dall'alto: la barra di stato (tinta unita espresso)
 * si fonde con il primo tratto, la riga del marchio sta sopra .80 o più, la
 * fascia della foto a .44, dal filo d'oro in giù .82 e più (misurato: AA su
 * una foto tutta bianca). Le posizioni seguono l'intestazione: marchio fino
 * a +58px, fascia fino a +138px, filo d'oro a +160px.
 */
const photoVeil = (rgb: string, a: readonly [number, number, number, number, number]) =>
  `linear-gradient(180deg, ` +
  `rgba(${rgb},${a[0]}) 0px, ` +
  `rgba(${rgb},${a[1]}) calc(${SAFE_TOP} + 60px), ` +
  `rgba(${rgb},${a[2]}) calc(${SAFE_TOP} + 90px), ` +
  `rgba(${rgb},${a[2]}) calc(${SAFE_TOP} + 128px), ` +
  `rgba(${rgb},${a[3]}) calc(${SAFE_TOP} + 156px), ` +
  `rgba(${rgb},${a[4]}) 100%)`;
/** Espresso #2A2620. */
const PHOTO_VEIL_LIGHT = photoVeil('42,38,32', [0.88, 0.8, 0.44, 0.82, 0.86]);
/** Notte #1a1a1a, più fitto: in scuro la foto è più scura. */
const PHOTO_VEIL_DARK = photoVeil('26,26,26', [0.92, 0.84, 0.52, 0.86, 0.9]);

/** Il viraggio al marrone ("una foto leggera con un filtro marroncino"); in scuro più scura. */
const PHOTO_FILTER =
  '[filter:sepia(.45)_saturate(.8)_brightness(.82)] dark:[filter:sepia(.45)_saturate(.8)_brightness(.68)]';

/** L'ombra leggera sotto il testo sopra la foto (la stessa del carosello). */
const TEXT_SHADOW = '[text-shadow:0_1px_2px_rgba(0,0,0,0.45)]';

type PhotoStatus = 'loading' | 'loaded' | 'failed';

/**
 * La foto e il velo, sotto il contenuto. Decorativa (aria-hidden, alt
 * vuoto). Montata con key = indirizzo: un'altra foto riparte da zero.
 */
const MastheadPhoto: React.FC<{
  photo: ApartmentPhotoProps;
  loaded: boolean;
  onLoad: () => void;
  onError: () => void;
}> = ({ photo, loaded, onLoad, onError }) => {
  const imgRef = useRef<HTMLImageElement>(null);
  // Già in cache e decodificata prima che React ascoltasse: la si mostra.
  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) onLoad();
    // Solo al montaggio: una foto nuova rimonta il componente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div aria-hidden="true" data-masthead-photo="" className="pointer-events-none absolute inset-0 overflow-hidden">
      <img
        ref={imgRef}
        src={photo.src}
        srcSet={photo.srcSet}
        sizes={photo.sizes}
        alt=""
        decoding="async"
        // In minuscolo: con React 18 "fetchPriority" darebbe un avviso.
        {...{ fetchpriority: 'high' }}
        onLoad={onLoad}
        onError={onError}
        className={cn(
          'absolute inset-0 h-full w-full object-cover motion-safe:transition-opacity motion-safe:duration-300',
          PHOTO_FILTER,
          loaded ? 'opacity-100' : 'opacity-0',
        )}
        style={{ objectPosition: 'center 55%' }}
      />
      <span className="absolute inset-0 dark:hidden" style={{ background: PHOTO_VEIL_LIGHT }} />
      <span className="absolute inset-0 hidden dark:block" style={{ background: PHOTO_VEIL_DARK }} />
    </div>
  );
};

// ---- L'intestazione intera -----------------------------------------------------------

export interface HomeMastheadProps {
  /** L'occhiello d'oro ("BUONGIORNO", "TI ASPETTIAMO", "GRAZIE DELLA VISITA"). */
  eyebrow: React.ReactNode;
  /** Il saluto grande in Fraunces ("Ciao Giulia"): è l'h1 della pagina. */
  title: React.ReactNode;
  /** La riga sotto il saluto ("Notte 3 di 7 · Appartamento 101"). */
  sub?: React.ReactNode;
  /**
   * Spazio in fondo per la card che si sovrappone (StayCard, margine -72px):
   * vero di default. false = intestazione chiusa, senza card sopra.
   */
  overlap?: boolean;
  /** L'avatar con il menu dell'account: vero di default (viste con l'ospite dentro). */
  account?: boolean;
  /**
   * La foto principale dell'appartamento prenotato (booking.apartmentPhotoUrl,
   * l'originale https sul channel manager: qui si carica ridimensionata).
   * Assente, null o non https: l'intestazione di sempre, senza foto.
   */
  photoUrl?: string | null;
  className?: string;
}

const HomeMasthead: React.FC<HomeMastheadProps> = ({
  eyebrow,
  title,
  sub,
  overlap = true,
  account = true,
  photoUrl,
  className,
}) => {
  const photo = useMemo(() => apartmentPhotoProps(photoUrl), [photoUrl]);
  const src = photo ? photo.src : '';
  // Lo stato vale per UNA foto: con un indirizzo nuovo si riparte da
  // "loading" (anche per una foto che prima non si era caricata).
  const [photoState, setPhotoState] = useState<{ src: string; status: PhotoStatus }>({ src, status: 'loading' });
  let status = photoState.status;
  if (photoState.src !== src) {
    status = 'loading';
    setPhotoState({ src, status });
  }
  const settle = (next: PhotoStatus) => () =>
    setPhotoState((prev) => (prev.src === src ? { src, status: next } : prev));
  // La fascia c'è già mentre la foto arriva (niente salto); se non arriva,
  // si torna all'intestazione di prima.
  const withPhoto = !!photo && status !== 'failed';
  const shown = !!photo && status === 'loaded';

  return (
    <header
      className={cn(
        'relative flex flex-col gap-[22px] bg-nva-espresso px-4 dark:bg-nva-night',
        overlap ? 'pb-[92px]' : 'pb-7',
        className,
      )}
      style={{ paddingTop: `calc(${SAFE_TOP} + 14px)` }}
    >
      {photo && withPhoto ? (
        <MastheadPhoto
          key={src}
          photo={photo}
          loaded={shown}
          onLoad={settle('loaded')}
          onError={settle('failed')}
        />
      ) : null}
      {/* relative: il contenuto sta sopra la foto (senza creare un contesto
          di sovrapposizione: il menu dell'avatar deve restare sopra la card). */}
      <BrandBar account={account} variant={shown ? 'overlay' : 'solid'} className="relative" />
      {withPhoto ? (
        <div aria-hidden="true" data-masthead-window="" className="flex-shrink-0" style={{ height: PHOTO_WINDOW_PX }} />
      ) : null}
      <div className="relative flex flex-col gap-2">
        <GoldRule />
        <Eyebrow tone="onDark" className={shown ? TEXT_SHADOW : undefined}>
          {eyebrow}
        </Eyebrow>
        <h1
          className={cn(
            'm-0 break-words font-display text-[34px] font-bold leading-[1.05] text-nva-bone',
            shown && TEXT_SHADOW,
          )}
        >
          {title}
        </h1>
        {sub ? (
          <p
            className={cn(
              'm-0 text-[14.5px] leading-snug',
              shown ? cn('text-nva-parchment', TEXT_SHADOW) : 'text-nva-foot',
            )}
          >
            {sub}
          </p>
        ) : null}
      </div>
    </header>
  );
};

export default HomeMasthead;
